import { useState, useEffect } from 'react';
import { useRef } from 'react';
import { API_BASE_URL } from '../config/api';
import { useAuth } from '@/react-app/context/AuthContext';

// Instrumentação forte para verificar carregamento do módulo em produção e versão ativa
const BUILD_TIME = import.meta.env.VITE_BUILD_TIME;
const USE_API_BUILD_STAMP = BUILD_TIME;
// Este log deve aparecer UMA vez quando o bundle real carregar.
console.log(
  '🧪 [useApi MODULE INIT] build stamp:',
  USE_API_BUILD_STAMP,
  'API_BASE_URL:',
  API_BASE_URL,
);

// Dedupe por URL para evitar segunda chamada em StrictMode apenas por instância.
// Mantido global para reutilização em cenário avançado, mas não bloqueia outras URLs.
const initialFetchMap: Record<string, boolean> =
  (globalThis as any).__airtrust_initialFetchMap || {};
(globalThis as any).__airtrust_initialFetchMap = initialFetchMap;

const buildFullUrl = (url: string): string => {
  // Absolute URL: return as-is
  if (/^https?:\/\//i.test(url)) return url;

  // Normalizar base (garantir sem barra final dupla)
  const base = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;

  // Remover barras iniciais múltiplas
  let path = url.replace(/^\/+/, '');

  // Se base termina com /api e path começa com api/, remover prefixo duplicado "api/"
  if (base.endsWith('/api') && path.toLowerCase().startsWith('api/')) {
    path = path.slice(4); // remove 'api/'
  }

  // Se path começa com 'api/' mas base NÃO termina com '/api', manter (caso tenha vindo de outra convenção)
  // Garantir que sempre haja uma barra entre base e path
  const full = `${base}/${path}`;

  console.log('🔧 [buildFullUrl] Input:', url);
  console.log('🔧 [buildFullUrl] base:', base);
  console.log('🔧 [buildFullUrl] path (normalized):', path);
  console.log('🔧 [buildFullUrl] Result:', full);

  return full;
};

interface UseApiOptions {
  retry?: number;
  retryDelay?: number;
  enabled?: boolean;
  /**
   * Evita disparo duplicado em React StrictMode (efeito montado 2x em dev).
   * Mantém primeira chamada e ignora a segunda se ainda na mesma render inicial.
   * Default: true
   */
  dedupeInitial?: boolean;
  /**
   * Quando true exige presença de token antes de realizar fetch.
   * Para endpoints públicos (ex: /qualificacoes/historico, /qualificacoes/tipos) usar false.
   * Default: true.
   */
  requireAuth?: boolean;
}

export function useApi<T>(url: string, options: UseApiOptions = {}) {
  const {
    retry = 3,
    retryDelay = 1000,
    enabled = true,
    dedupeInitial = true,
    requireAuth = true,
  } = options;
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { logout, token: authToken } = useAuth();
  // Guarda local para evitar disparo duplicado em StrictMode (apenas por instância)
  const hasFetchedInitialRef = { current: false };

  const fetchData = async (attemptNumber = 0) => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Construir URL completa com regras robustas e sem duplicar "/api":
      // - Se começa com http, usa como está
      // - Se começa com "/api/", usar a ORIGEM da API (API_BASE_URL sem "/api") + url
      // - Se começa com "/" mas NÃO com "/api", prefixar "/api"
      // - Se começa com "/api", usar ORIGEM + url (evita duplicação)
      // - Caso contrário (paths relativos), prefixar com "/api/"
      const fullUrl = buildFullUrl(url);

      // Se requisição exige autenticação e token ainda não carregou, aguardar.
      // Evita spam de chamadas 401 que derrubam sessão.
      const localStorageKeysEarlyCheck = [
        'airtrust_token',
        'token',
        'auth_token',
        'accessToken',
        'access_token',
        'airtrust_access_token',
      ];
      if (requireAuth) {
        const anyTokenPresent =
          authToken || localStorageKeysEarlyCheck.some((k) => !!localStorage.getItem(k));
        if (!anyTokenPresent) {
          if (attemptNumber === 0) {
            console.warn(
              `⏳ [useApi] Aguardando token antes de iniciar fetch para ${url} (adiando tentativa)`,
            );
          }
          // Mantém loading true para não parecer lista vazia
          setTimeout(() => fetchData(attemptNumber), 500);
          return; // re-tentar em breve
        }
      }

      console.log(`🔍 [useApi] Path original: ${url}`);
      console.log(
        `🔍 [useApi] Fetchando: ${fullUrl} (tentativa ${attemptNumber + 1}/${retry + 1})`,
      );
      console.log(`🔍 [useApi DEBUG] AuthContext token:`, authToken ? 'SIM' : 'NÃO');

      // Buscar token do AuthContext (prioritário) ou fallback para tokens legados
      const localStorageKeys = [
        'airtrust_token',
        'token',
        'auth_token',
        'accessToken',
        'access_token',
        'airtrust_access_token',
      ];

      let lsToken: string | null = null;
      for (const key of localStorageKeys) {
        const value = localStorage.getItem(key);
        if (value) {
          console.log(
            `🔑 [useApi] Token encontrado em localStorage["${key}"] (${value.substring(0, 20)}...)`,
          );
          lsToken = value;
          break;
        }
      }

      const token = authToken || lsToken;

      const isLocalhost =
        typeof window !== 'undefined' &&
        (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
        console.log(`🔐 [useApi] Token usado no header (${token.substring(0, 20)}...)`);
      } else {
        if (requireAuth) {
          console.warn(`⚠️  [useApi] Nenhum token encontrado para ${fullUrl}`);
        }
      }

      // Removido header X-Dev-Auth-Bypass (causava falha CORS preflight). Caso necessário, adicionar whitelist no backend antes de reintroduzir.

      const response = await fetch(fullUrl, { headers });

      // Tratar 401 Unauthorized: fazer logout automático
      if (response.status === 401) {
        console.error(`🔒 [useApi] 401 Unauthorized em ${fullUrl} - Fazendo logout`);
        logout();
        window.location.href = '/login'; // Redirecionar para login
        throw new Error('Sessão expirada. Por favor, faça login novamente.');
      }

      if (!response.ok) {
        const result = await response.json().catch(() => ({}));
        throw new Error(result.error || `Erro ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      console.log('🔍 [useApi] Result recebido:', {
        hasSuccess: result.success !== undefined,
        success: result.success,
        hasStats: result.stats !== undefined,
        hasMeta: result.meta !== undefined,
        hasPagination: result.pagination !== undefined,
        statsValue: result.stats,
      });

      if (result.success !== undefined) {
        if (result.success) {
          // Se a resposta contém campos adicionais (pagination, stats, meta), preservar objeto completo
          const hasExtended =
            typeof result === 'object' &&
            (result.pagination !== undefined ||
              result.stats !== undefined ||
              result.meta !== undefined);
          console.log(
            '🔍 [useApi] hasExtended:',
            hasExtended,
            'stats?',
            result.stats !== undefined,
          );
          if (hasExtended) {
            setData(result as T);
            console.log(`✅ [useApi] Sucesso (extended): ${fullUrl}`, result);
          } else {
            setData(result.data as T);
            console.log(`✅ [useApi] Sucesso: ${fullUrl}`, result.data);
          }
        } else {
          throw new Error((result as any).error || 'Erro desconhecido');
        }
      } else if (Array.isArray(result)) {
        setData(result as T);
        console.log(`✅ [useApi] Sucesso (array): ${fullUrl}`, result);
      } else if (typeof result === 'object') {
        setData(result as T);
        console.log(`✅ [useApi] Sucesso (object): ${fullUrl}`, result);
      } else {
        throw new Error('Formato de resposta inválido');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido';

      // Não fazer retry em erros de autenticação (401) - já foi tratado acima
      const isAuthError = errorMessage.includes('Sessão expirada') || errorMessage.includes('401');

      // Retry logic (exceto para erros de autenticação)
      if (!isAuthError && attemptNumber < retry) {
        console.warn(
          `⚠️  [useApi] Erro em ${url}, retry em ${retryDelay}ms... (${
            attemptNumber + 1
          }/${retry})`,
        );
        setTimeout(() => fetchData(attemptNumber + 1), retryDelay);
      } else {
        console.error(`❌ [useApi] Erro final em ${url}:`, errorMessage);
        setError(errorMessage);
        setLoading(false);
      }
    } finally {
      if (attemptNumber === 0 || error) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!enabled) return;
    if (dedupeInitial && process.env.NODE_ENV !== 'production') {
      if (hasFetchedInitialRef.current) return;
      hasFetchedInitialRef.current = true;
    }
    // Dedupe adicional por URL (impede repetição dentro mesma instância dev StrictMode)
    if (process.env.NODE_ENV !== 'production' && initialFetchMap[url]) return;
    initialFetchMap[url] = true;
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, enabled, authToken]);

  return { data, loading, error, refetch: () => fetchData() };
}

export function useApiMutation<T>() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { logout, token: authToken } = useAuth();

  const mutate = async (url: string, options: RequestInit): Promise<T> => {
    try {
      setLoading(true);
      setError(null);

      const fullUrl = buildFullUrl(url);

      // Buscar token do AuthContext (prioritário) ou fallback para tokens legados
      const localStorageKeys = [
        'airtrust_token',
        'token',
        'auth_token',
        'accessToken',
        'access_token',
        'airtrust_access_token',
      ];

      let lsToken: string | null = null;
      for (const key of localStorageKeys) {
        const value = localStorage.getItem(key);
        if (value) {
          console.log(
            `🔑 [useApiMutation] Token encontrado em localStorage["${key}"] (${value.substring(
              0,
              20,
            )}...)`,
          );
          lsToken = value;
          break;
        }
      }

      const token = authToken || lsToken;

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...(options.headers as Record<string, string>),
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
        console.log(`🔐 [useApiMutation] Token usado no header (${token.substring(0, 20)}...)`);
      } else {
        console.warn(`⚠️  [useApiMutation] Nenhum token encontrado para ${fullUrl}`);
      }

      const response = await fetch(fullUrl, {
        ...options,
        headers,
      });

      // Tratar 401 Unauthorized: fazer logout automático
      if (response.status === 401) {
        console.error(`🔒 [useApiMutation] 401 Unauthorized em ${fullUrl} - Fazendo logout`);
        logout();
        window.location.href = '/login';
        throw new Error('Sessão expirada. Por favor, faça login novamente.');
      }

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Erro na requisição');
      }

      if (!result.success) {
        throw new Error(result.error || 'Erro desconhecido');
      }

      return result.data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return { mutate, loading, error };
}

// ================== DEBUG URL TESTS (somente desenvolvimento) ==================
if (
  typeof window !== 'undefined' &&
  import.meta.env.MODE === 'development' &&
  !(globalThis as any).__useApi_url_tests_executed
) {
  (globalThis as any).__useApi_url_tests_executed = true;
  console.log('🧪 [useApi URL TEST] Iniciando casos...');
  const samples = [
    '/api/qualificacoes/tipos?limit=200',
    '/categorias',
    '/api/qualificacoes/historico?limit=50&page=1',
    'funcionarios',
    '/api/funcionarios',
  ];
  for (const s of samples) {
    console.log('🧪 [useApi URL TEST] Input:', s, '->', buildFullUrl(s));
  }
  console.log('✅ [useApi URL TEST] Concluído');
}
